<template lang='pug'>
  #main(ref='main' :class='{pointer: isPointing}')
</template>

<script>
import * as THREE from 'three'
import { gsap } from 'gsap'

export default {
  name: 'Main',
  data () {
    return {
      camera: null,
      cameraTarget: null,
      renderer: null,
      lights: [],
      texts: [],
      scene: null,
      animationTimeline: null,
      animationFrame: null,
      typeface: './fonts/Paytone_One_Regular.json',
      backgroundColor: '#FB031C',
      text: {},
      raycaster: null,
      isPointing: false
    }
  },
  computed: {
    defaultTextOptions: () => {
      return {
        font: './fonts/Paytone_One_Regular.json',
        height: 10,
        size: 28,
        curveSegments: 4,
        bevelEnabled: false,
        bevelThickness: 1,
        bevelSize: 1.5,
        bevelSegments: 3,
        colorFront: 0xffffff,
        colorSides: 0x0,
        position: { x: 0, y: 0, z: 800 },
        rotation: { x: 0, y: 0, z: 0 }
      }
    },
    ww: { cache: false, get () { return window.innerWidth } },
    wh: { cache: false, get () { return window.innerHeight } },
    links: () => {
      return {
        linkedin: 'https://linkedin.com/in/anthonydamota',
        twitter: 'https://twitter.com/AkdM_',
        email: 'mailto:contact@damota.me'
      }
    }
  },
  methods: {
    init: function () {
      // Create scene
      this.scene = new THREE.Scene()
      this.scene.fog = new THREE.Fog(0xFB031C, 0, 1000)

      // Setup renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true })
      // this.renderer.setClearColor(this.backgroundColor)
      this.renderer.setSize(this.ww, this.wh)

      this.$refs.main.appendChild(this.renderer.domElement)

      // Setup camera
      this.camera = new THREE.PerspectiveCamera(30, this.ww / this.wh, 1, 725)
      this.camera.position.set(0, 0, 950)

      // Setup Raycaster for conflict on click
      this.raycaster = new THREE.Raycaster()

      // this.cameraTarget = new THREE.Vector3(0, 0, 0)

      // let helper = new THREE.CameraHelper(this.camera)
      // this.scene.add(helper)

      // Setup lights
      // this.lights[0] = new THREE.DirectionalLight(0xffffff, 1)
      // this.lights[0].position.set(0, 0, 1)
      // this.scene.add(this.lights[0])

      this.lights[1] = new THREE.AmbientLight(0xffffff, 1)
      this.scene.add(this.lights[1])

      this.render()
    },
    loadTexts: function () {
      const fontLoader = new THREE.FontLoader()
      fontLoader.load(this.typeface, (font) => {
        // D* **** //
        this.texts[0] = {}
        this.texts[0].geometry = new THREE.TextGeometry('D', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[0].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0x0C183D }), // front
          new THREE.MeshBasicMaterial({ color: 0x0C2FAC }) // sides
        ]

        // Text mesh configuration
        this.texts[0].mesh = new THREE.Mesh(this.texts[0].geometry, this.texts[0].textMaterials)
        this.texts[0].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, 200)
        this.texts[0].mesh.rotation.set(this.defaultTextOptions.rotation.x, 1.55, this.defaultTextOptions.rotation.z)
        this.texts[0].geometry.center() // make anchor in the middle of the text

        // *A **** //
        this.texts[1] = {}
        this.texts[1].geometry = new THREE.TextGeometry('A', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[1].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0xffffff }), // front
          new THREE.MeshBasicMaterial({ color: 0x00B3E4 }) // sides
        ]

        // Text mesh configuration
        this.texts[1].mesh = new THREE.Mesh(this.texts[1].geometry, this.texts[1].textMaterials)
        this.texts[1].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, 900)
        this.texts[1].mesh.rotation.set(this.defaultTextOptions.rotation.x, 0.8, this.defaultTextOptions.rotation.z)
        this.texts[1].geometry.center() // make anchor in the middle of the text

        // ** M*** //
        this.texts[2] = {}
        this.texts[2].geometry = new THREE.TextGeometry('M', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[2].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0x0074E4 }), // front
          new THREE.MeshBasicMaterial({ color: 0x00A9E4 }) // sides
        ]

        // Text mesh configuration
        this.texts[2].mesh = new THREE.Mesh(this.texts[2].geometry, this.texts[2].textMaterials)
        this.texts[2].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, 885)
        this.texts[2].mesh.rotation.set(this.defaultTextOptions.rotation.x, -0.8, this.defaultTextOptions.rotation.z)
        this.texts[2].geometry.center() // make anchor in the middle of the text

        // ** *O** //
        this.texts[3] = {}
        this.texts[3].geometry = new THREE.TextGeometry('O', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[3].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0xF34407 }), // front
          new THREE.MeshBasicMaterial({ color: 0x8E2500 }) // sides
        ]

        // Text mesh configuration
        this.texts[3].mesh = new THREE.Mesh(this.texts[3].geometry, this.texts[3].textMaterials)
        this.texts[3].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, this.defaultTextOptions.position.z)
        this.texts[3].mesh.rotation.set(this.defaultTextOptions.rotation.x, -2.0, this.defaultTextOptions.rotation.z)
        this.texts[3].geometry.center() // make anchor in the middle of the text

        // ** **T* //
        this.texts[4] = {}
        this.texts[4].geometry = new THREE.TextGeometry('T', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[4].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0x0 }), // front
          new THREE.MeshBasicMaterial({ color: 0x2F2F2F }) // sides
        ]

        // Text mesh configuration
        this.texts[4].mesh = new THREE.Mesh(this.texts[4].geometry, this.texts[4].textMaterials)
        this.texts[4].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, 300)
        this.texts[4].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        this.texts[4].geometry.center() // make anchor in the middle of the text

        // ** ***A //
        this.texts[5] = {}
        this.texts[5].geometry = new THREE.TextGeometry('A', {
          font: font,
          height: this.defaultTextOptions.height,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 1
        })

        // Text materials
        this.texts[5].textMaterials = [
          new THREE.MeshBasicMaterial({ color: 0xFED700 }), // front
          new THREE.MeshBasicMaterial({ color: 0xDAB900 }) // sides
        ]

        // Text mesh configuration
        this.texts[5].mesh = new THREE.Mesh(this.texts[5].geometry, this.texts[5].textMaterials)
        this.texts[5].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y, 950)
        this.texts[5].mesh.rotation.set(this.defaultTextOptions.rotation.x, -0.8, this.defaultTextOptions.rotation.z)
        this.texts[5].geometry.center() // make anchor in the middle of the text

        // anthony //
        this.texts[6] = {}
        this.texts[6].geometry = new THREE.TextGeometry('anthony', {
          font: font,
          height: 1,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 0
        })

        // Text materials
        this.texts[6].textMaterials = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 })

        // Text mesh configuration
        this.texts[6].mesh = new THREE.Mesh(this.texts[6].geometry, this.texts[6].textMaterials)
        this.texts[6].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y + 20, 300)
        this.texts[6].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        this.texts[6].geometry.center() // make anchor in the middle of the text

        // DA MOTA //
        this.texts[7] = {}
        this.texts[7].geometry = new THREE.TextGeometry('DA MOTA', {
          font: font,
          height: 1,
          size: this.defaultTextOptions.size,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 0
        })

        // Text materials
        this.texts[7].textMaterials = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 })

        // Text mesh configuration
        this.texts[7].mesh = new THREE.Mesh(this.texts[7].geometry, this.texts[7].textMaterials)
        this.texts[7].mesh.position.set(this.defaultTextOptions.position.x, this.defaultTextOptions.position.y - 15, this.defaultTextOptions.position.z - 200)
        this.texts[7].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        // this.texts[7].mesh.userData = { URL: 'https://google.fr' }
        this.texts[7].geometry.center() // make anchor in the middle of the text

        // LinkedIn
        this.texts[8] = {}
        this.texts[8].geometry = new THREE.TextGeometry('LinkedIn', {
          font: font,
          height: 1,
          size: 3,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 0
        })

        // Text materials
        this.texts[8].textMaterials = new THREE.MeshBasicMaterial({ color: 0x0077b5, transparent: true, opacity: 0 })

        // Text mesh configuration
        this.texts[8].mesh = new THREE.Mesh(this.texts[8].geometry, this.texts[8].textMaterials)
        this.texts[8].mesh.position.set(this.defaultTextOptions.position.x - 30, this.defaultTextOptions.position.y + this.texts[7].mesh.position.y - 30, this.defaultTextOptions.position.z - 200)
        this.texts[8].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        this.texts[8].geometry.center() // make anchor in the middle of the text
        this.texts[8].mesh.name = 'linkedin'

        // Twitter
        this.texts[9] = {}
        this.texts[9].geometry = new THREE.TextGeometry('Twitter', {
          font: font,
          height: 1,
          size: 3,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 0
        })

        // Text materials
        this.texts[9].textMaterials = new THREE.MeshBasicMaterial({ color: 0x1da1f2, transparent: true, opacity: 0 })

        // Text mesh configuration
        this.texts[9].mesh = new THREE.Mesh(this.texts[9].geometry, this.texts[9].textMaterials)
        this.texts[9].mesh.position.set(this.texts[8].mesh.position.x + 30, this.defaultTextOptions.position.y + this.texts[7].mesh.position.y - 30, this.defaultTextOptions.position.z - 200)
        this.texts[9].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        this.texts[9].geometry.center() // make anchor in the middle of the text
        this.texts[9].mesh.name = 'twitter'

        // Email
        this.texts[10] = {}
        this.texts[10].geometry = new THREE.TextGeometry('Contact', {
          font: font,
          height: 1,
          size: 3,
          curveSegments: this.defaultTextOptions.curveSegments,
          bevelEnabled: this.defaultTextOptions.bevelEnabled,
          material: 0,
          extrudeMaterial: 0
        })

        // Text materials
        this.texts[10].textMaterials = new THREE.MeshBasicMaterial({ color: 0xfffc00, transparent: true, opacity: 0 })

        // Text mesh configuration
        this.texts[10].mesh = new THREE.Mesh(this.texts[10].geometry, this.texts[10].textMaterials)
        this.texts[10].mesh.position.set(this.texts[9].mesh.position.x + 30, this.defaultTextOptions.position.y + this.texts[7].mesh.position.y - 30, this.defaultTextOptions.position.z - 200)
        this.texts[10].mesh.rotation.set(this.defaultTextOptions.rotation.x, this.defaultTextOptions.rotation.y, this.defaultTextOptions.rotation.z)
        this.texts[10].geometry.center() // make anchor in the middle of the text
        this.texts[10].mesh.name = 'email'

        // Add the texts to the scene and play the animations
        // this.scene.add(this.texts[0].mesh)

        this.firstLetter()
      })
    },
    firstLetter: function () {
      this.scene.background = new THREE.Color(0xFB031C)
      this.scene.add(this.texts[0].mesh)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[0].mesh.position, {
        z: 800,
        ease: 'power1.inOut',
        duration: 1.5
      }).to(this.texts[0].mesh.rotation, {
        y: 0,
        ease: 'power1.inOut',
        duration: 0.75
      }, '-=0.75').eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[0].mesh)
        this.secondLetter()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    secondLetter: function () {
      this.scene.add(this.texts[1].mesh)
      this.scene.background = new THREE.Color(0x0074E4)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[1].mesh.position, {
        z: this.defaultTextOptions.position.z,
        ease: 'power1.inOut',
        duration: 0.5
      }).to(this.texts[1].mesh.rotation, {
        y: 0,
        ease: 'power1.in',
        duration: 0.25
      }, '-=0.35').eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[1].mesh)
        this.thirdLetter()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    thirdLetter: function () {
      this.scene.add(this.texts[2].mesh)
      this.scene.background = new THREE.Color(0xffffff)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[2].mesh.rotation, {
        y: -0.3,
        ease: 'power1.out',
        duration: 0.3
      }).eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[2].mesh)
        this.fourthLetter()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    fourthLetter: function () {
      this.scene.add(this.texts[3].mesh)
      this.scene.background = new THREE.Color(0x272831)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[3].mesh.rotation, {
        y: 4,
        ease: 'power0',
        duration: 0.40
      }).eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[3].mesh)
        this.fifthLetter()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    fifthLetter: function () {
      this.scene.add(this.texts[4].mesh)
      this.scene.background = new THREE.Color(0xFED700)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[4].mesh.position, {
        z: this.texts[4].mesh.position.z + 180,
        duration: 0,
        delay: 0.15,
        onComplete: () => {
          this.texts[4].mesh.material[0].color = new THREE.Color(0x555b5f)
          this.texts[4].mesh.material[1].color = new THREE.Color(0x3F4649)
        }
      }).to(this.texts[4].mesh.position, {
        z: this.texts[4].mesh.position.z + (180 * 2),
        duration: 0,
        delay: 0.15,
        onComplete: () => {
          this.texts[4].mesh.material[0].color = new THREE.Color(0x4c62b7)
          this.texts[4].mesh.material[1].color = new THREE.Color(0x374886)
        }
      }).to(this.texts[4].mesh.position, {
        z: this.texts[4].mesh.position.z + (180 * 3),
        duration: 0,
        delay: 0.15,
        onComplete: () => {
          this.texts[4].mesh.material[0].color = new THREE.Color(0xFB031C)
          this.texts[4].mesh.material[1].color = new THREE.Color(0x9d0003)
        }
      }).to(this.texts[4].mesh.position, {
        z: this.texts[4].mesh.position.z + (180 * 4),
        duration: 0,
        delay: 0.15,
        onComplete: () => {
          this.texts[4].mesh.material[0].color = new THREE.Color(0x0)
          this.texts[4].mesh.material[1].color = new THREE.Color(0x2a292a)
        }
      }).eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[4].mesh)
        this.sixthLetter()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    sixthLetter: function () {
      this.scene.add(this.texts[5].mesh)
      this.scene.background = new THREE.Color(0xF34407)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[5].mesh.position, {
        z: this.defaultTextOptions.position.z,
        ease: 'power0',
        duration: 0.55
      }).to(this.texts[5].mesh.rotation, {
        y: 0,
        ease: 'power0',
        duration: 0.5
      }, '-=0.55').eventCallback('onComplete', () => {
        // console.info('onComplete')
        this.scene.remove(this.texts[5].mesh)
        this.anthonyDaMotaText()
        // window.cancelAnimationFrame(this.animationFrame)
      })
    },
    anthonyDaMotaText: function () {
      this.scene.fog = false
      this.scene.add(this.texts[6].mesh)
      this.scene.add(this.texts[7].mesh)
      this.scene.background = new THREE.Color(0x23252a)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[6].mesh.material, {
        opacity: 1,
        ease: 'power1.inOut',
        duration: 3
      }).to(this.texts[7].mesh.material, {
        opacity: 1,
        ease: 'power1.inOut',
        duration: 3
      }, '-=3').eventCallback('onComplete', () => {
        // console.info('onComplete')
        // this.scene.remove(this.texts[6].mesh)
        this.comingSoonText()
      })
    },
    comingSoonText: function () {
      this.scene.add(this.texts[8].mesh)
      this.animationTimeline = gsap.timeline()
      this.animationTimeline.to(this.texts[8].mesh.material, {
        opacity: 1,
        ease: 'power1.inOut',
        duration: 1
      }).eventCallback('onStart', () => {
        this.scene.add(this.texts[9].mesh)
        this.animationTimeline = gsap.timeline()
        this.animationTimeline.to(this.texts[9].mesh.material, {
          opacity: 1,
          ease: 'power1.inOut',
          duration: 1,
          delay: 0.2
        }).eventCallback('onStart', () => {
          this.scene.add(this.texts[10].mesh)
          this.animationTimeline = gsap.timeline()
          this.animationTimeline.to(this.texts[10].mesh.material, {
            opacity: 1,
            ease: 'power1.inOut',
            duration: 1,
            delay: 0.2
          }).eventCallback('onComplete', () => {
            // window.cancelAnimationFrame(this.animationFrame)
          })
        })
      })
    },
    render: function () {
      // console.info('Rendering…')
      this.camera.updateProjectionMatrix()
      this.renderer.clear()
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.render(this.scene, this.camera)

      this.animate()
      this.loadTexts()
    },
    resized: function () {
      // console.info('resize()')
      this.camera.aspect = this.ww / this.wh
      this.camera.updateProjectionMatrix()
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(this.ww, this.wh)
      this.renderer.render(this.scene, this.camera)
    },
    animate: function () {
      // console.info('animate()')
      this.animationFrame = window.requestAnimationFrame(this.animate)
      this.renderer.render(this.scene, this.camera)
    },
    mouseEvent (event) {
      let mouse = new THREE.Vector2()
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
      this.raycaster.setFromCamera(mouse, this.camera)
      let intersects = this.raycaster.intersectObjects(this.scene.children)
      if (intersects.length > 0) {
        const objectName = intersects[0].object.name
        const link = this.links[objectName]
        if (link) {
          if (event.type === 'mousemove') this.isPointing = true
          if (event.type === 'click') window.open(link)
        } else {
          this.isPointing = false
        }
      } else {
        this.isPointing = false
      }
    }
  },
  mounted () {
    this.init()
    window.addEventListener('resize', this.resized, false)
    window.addEventListener('click', this.mouseEvent, false)
    window.addEventListener('mousemove', this.mouseEvent, false)
  }
}
</script>

<style lang='stylus'>
  body:after {
    content: '';
    background-image: url('https://s3-us-west-2.amazonaws.com/s.cdpn.io/756881/gifnoise.gif');
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 999;
    opacity: 0.08;
    will-change: auto;
    transform: translate3d(0, 0, 0);
    pointer-events: none;
  }
  #main.pointer {
    cursor: pointer;
  }
</style>
